/**
 * Currency Utilities
 * Formatting and currency symbol handling
 */

export const CURRENCY_SYMBOLS: Record<string, string> = {
  'EUR': '€',
  'USD': '$',
  'RSD': 'дин.',
  'GBP': '£',
  'JPY': '¥',
  'AUD': 'A$',
  'CAD': 'C$'
};

export const CURRENCY_NAMES: Record<string, string> = {
  'EUR': 'Euro',
  'USD': 'US Dollar',
  'RSD': 'Serbian Dinar',
  'GBP': 'British Pound',
  'JPY': 'Japanese Yen',
  'AUD': 'Australian Dollar',
  'CAD': 'Canadian Dollar'
};

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Get currency name
 */
export function getCurrencyName(currency: string): string {
  return CURRENCY_NAMES[currency] || currency;
}

/**
 * Format amount with currency
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EUR',
  showSymbol: boolean = true,
  decimals: number = 2
): string {
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = amount.toFixed(decimals);

  // Format based on currency conventions
  if (currency === 'USD' || currency === 'EUR' || currency === 'GBP') {
    return showSymbol ? `${symbol}${formattedAmount}` : `${formattedAmount} ${currency}`;
  }

  if (currency === 'RSD') {
    return showSymbol ? `${formattedAmount} ${symbol}` : `${formattedAmount} ${currency}`;
  }

  if (currency === 'JPY') {
    // Japanese Yen typically has no decimals
    const jpyAmount = Math.round(amount).toString();
    return showSymbol ? `${symbol}${jpyAmount}` : `${jpyAmount} ${currency}`;
  }

  // Default format
  return showSymbol ? `${symbol}${formattedAmount}` : `${formattedAmount} ${currency}`;
}

/**
 * Format bytes to human-readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Parse currency amount from string
 */
export function parseCurrency(amountString: string, currency: string = 'EUR'): number {
  // Remove currency symbols and whitespace
  const cleaned = amountString
    .replace(/[€$£¥]/g, '')
    .replace(/[^\d.,-]/g, '')
    .trim();

  // Handle different decimal separators
  const normalized = cleaned.replace(',', '.');
  return parseFloat(normalized) || 0;
}

